import moment from 'moment';
import { HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';
import * as script from 'scriptjs';

import { LOGIN_GOTO_QUERY_PARAM } from 'app/app.constants';
import {
  GRANTS_BUREAU_ID,
  GRANTS_BUREAU_NAME,
  GRANTS_BUREAU_URL_SEGMENT,
} from 'app/app.constants';
import { environment } from 'environments/environment';

export const loadScript = function (url: string, callback: any = undefined) {
  script(url, callback);
};

export const loadAppleAuthScript = function (callback) {
  loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', callback);
};

let googleMapsScriptPromise: Promise<boolean> | null = null;

export const isGoogleMapsLoaded = function (): boolean {
  return !!(typeof window !== 'undefined' && (window as any).google?.maps);
};

export const isGooglePlacesLoaded = function (): boolean {
  return !!(typeof window !== 'undefined' && (window as any).google?.maps?.places);
};

const ensurePlacesLibrary = async (): Promise<boolean> => {
  const maps = (window as any).google?.maps;
  if (maps?.importLibrary) {
    try {
      await maps.importLibrary('places');
    } catch (err) {
      // Places may already be present from an earlier load.
    }
    try {
      await maps.importLibrary('marker');
    } catch (err) {
      // Marker may already be present from an earlier load.
    }
  }
  return isGooglePlacesLoaded();
};

/**
 * Advanced markers require a map ID. DEMO_MAP_ID is Google's test id;
 * replace it with a Cloud Console map ID for production styling.
 */
export const GOOGLE_MAP_ID = 'DEMO_MAP_ID';

export const createAdvancedMarker = (options: { map?: any; position?: any; title?: string }): any => {
  const AdvancedMarkerElement = (window as any).google?.maps?.marker?.AdvancedMarkerElement;
  if (!AdvancedMarkerElement || options.position == null) return null;
  try {
    return new AdvancedMarkerElement({
      map: options.map,
      position: options.position,
      title: options.title || '',
      gmpClickable: true,
    });
  } catch (err) {
    return null;
  }
};

/**
 * Marker the overlapping-marker spiderfier can drive.
 * The spiderfier calls the legacy Marker methods; this object forwards them
 * to an AdvancedMarkerElement so google.maps.Marker is never constructed.
 */
export const createSpiderMarker = (position: { lat: number; lng: number }): any => {
  const maps = (window as any).google?.maps;
  const advanced = createAdvancedMarker({ position });
  if (!advanced || !maps?.MVCObject || !maps?.LatLng) return null;

  const latLng = new maps.LatLng(position.lat, position.lng);
  const shim: any = new maps.MVCObject();
  shim.advanced = advanced;
  shim.position = latLng;
  shim.map = null;
  shim.anchorPoint = new maps.Point(0, -32);
  shim._visible = true;
  shim._zIndex = null;

  shim.getPosition = function () { return this.position; };
  shim.setPosition = function (next) {
    this.position = next;
    advanced.position = next;
  };
  shim.getMap = function () { return this.map; };
  shim.setMap = function (map) {
    this.map = map;
    try {
      advanced.map = this._visible ? map : null;
    } catch (err) {
      advanced.map = null;
    }
  };
  shim.getVisible = function () { return this._visible; };
  shim.setVisible = function (visible) {
    this._visible = !!visible;
    advanced.map = this._visible ? this.map : null;
  };
  shim.getZIndex = function () { return this._zIndex; };
  shim.setZIndex = function (zIndex) {
    this._zIndex = zIndex;
    advanced.zIndex = zIndex;
  };
  shim.setIcon = function (icon) {
    if (!icon?.url) {
      advanced.content = null;
      return;
    }
    const width = icon.scaledSize?.width || 23;
    const height = icon.scaledSize?.height || 32;
    const img = document.createElement('img');
    img.src = icon.url;
    img.alt = '';
    img.width = width;
    img.height = height;
    advanced.content = img;
    advanced.gmpClickable = true;
    this.anchorPoint = new maps.Point(0, -height);
  };

  const forward = (name: string) => (event: any) => {
    maps.event.trigger(shim, name, event);
  };
  advanced.addEventListener('gmp-click', forward('click'));
  advanced.addEventListener('mouseenter', forward('mouseover'));
  advanced.addEventListener('mouseleave', forward('mouseout'));
  return shim;
};

/**
 * Loads the Google Maps JS API once (with Places) and notifies all callers.
 * Safe for concurrent use from maps, geocoders, and address autocomplete.
 *
 * loading=async fires the script load event before google.maps.importLibrary
 * exists, so readiness is the Google callback, not the script onload.
 */
export const loadGoogleMapScript = function (callback: any = undefined): Promise<boolean> {
  const finish = (ok: boolean) => {
    if (callback) {
      callback(ok);
    }
    return ok;
  };

  const maps = (window as any).google?.maps;
  if (isGoogleMapsLoaded() && (maps?.importLibrary || isGooglePlacesLoaded())) {
    return ensurePlacesLibrary().then((ok) => finish(ok));
  }

  if (!googleMapsScriptPromise) {
    const key = environment.GOOGLE_MAP_API_KEY;
    if (!key) {
      googleMapsScriptPromise = Promise.resolve(false);
    } else {
      googleMapsScriptPromise = new Promise<boolean>((resolve) => {
        const callbackName = `__omniGoogleMapsLoaded_${Date.now()}`;
        let settled = false;
        const done = (ok: boolean) => {
          if (settled) return;
          settled = true;
          try {
            delete (window as any)[callbackName];
          } catch (err) {
            (window as any)[callbackName] = undefined;
          }
          resolve(ok);
        };
        (window as any)[callbackName] = () => {
          ensurePlacesLibrary().then(done);
        };
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=${callbackName}`;
        script.onerror = () => done(false);
        document.head.appendChild(script);
      });
    }
  }

  return googleMapsScriptPromise.then((ok) => finish(ok));
};

export const getErrorMessage = function (err, message = '') {
  const nested = err?.error?.error?.message;
  if (nested) return nested;
  if (typeof err?.error?.message === 'string' && err.error.message) return err.error.message;
  if (typeof err?.error === 'string' && err.error.trim()) {
    const match = err.error.match(/<Message>([^<]+)<\/Message>/);
    if (match) return match[1];
    return err.error.trim();
  }
  if (err?.message) return err.message;
  if (err?.status === 0) return 'Network error — the upload may have been blocked.';
  if (err?.status) return `Request failed (${err.status}).`;
  return message || 'An unexpected error occurred.';
};

export const getDetailedErrorMessage = function (err, message = '') {
  return err.error && err.error.error && err.error.error.errors ? err.error.error.errors[0].message : message;
};

export const getFieldErrorMessage = function (err, message = '') {
  if (err.error && err.error.error && err.error.error.fieldErrors) {
    const fieldErrors = Object.values(err.error.error.fieldErrors);
    if (fieldErrors.length) {
      return capitalizeFirstLetter(fieldErrors[0]);
    }
  }
  return message;
};

export const isGrantsBureauId = (bureauId: string | null | undefined): boolean => (
  bureauId === GRANTS_BUREAU_ID
);

export const getBureauUrlSegment = (
  bureauId: string | null | undefined,
  bureauName: string,
): string => (
  isGrantsBureauId(bureauId) ? GRANTS_BUREAU_URL_SEGMENT : bureauName
);

export const resolveBureauFromUrlSegment = (company: any, segment: string) => {
  if (segment === GRANTS_BUREAU_URL_SEGMENT && company?.access_grants_sponsorships) {
    return { id: GRANTS_BUREAU_ID, name: GRANTS_BUREAU_NAME };
  }
  return (company?.bureaus || []).find((bureau) => bureau.name === segment) || null;
};

export const getCompanyBureauUrl = function (
  url: string,
  companyName: string,
  bureauName: string,
  bureauId: string = '',
): string {
  const bureauSegment = getBureauUrlSegment(bureauId, bureauName);
  if (url === '' || url === '/') {
    return [companyName, bureauSegment].join('.') + '/dashboard/';
  }
  if (url.startsWith('/')) {
    url = url.substr(1);
  }
  const parts = url.split('/');
  parts[0] = [companyName, bureauSegment].join('.') + '/' + parts[0];
  parts.unshift('');
  return parts.join('/');
};

export const getChecklistDueAt = function (checklist, event) {
  const dueWhen = checklist.due_when;
  const dueWhat = checklist.due_what;
  const dueDays = checklist.due_days || 0;
  if (event && dueWhen && dueWhat) {
    let what = null;
    if (dueWhat === 'request') {
      what = event.request ? event.request.approved_at : event.created_at;
    } else if (dueWhat === 'event' && event.common_event) {
      what = event.common_event.start_at;
    } else if (dueWhat && checklist[dueWhat]) {
      what = checklist[dueWhat].completed_at;
    }
    if (what) {
      if (dueWhen === 'before') {
        return moment(what).subtract(dueDays, 'days').toDate();
      }
      if (dueWhen === 'after') {
        return moment(what).add(dueDays, 'days').toDate();
      }
    }
  }
  return null;
};

export const buildFormData = function (data = {}) {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (data[key] instanceof Date) {
      formData.append(key, data[key].toISOString());
    } else if (typeof data[key] === 'boolean') {
      formData.append(key, data[key] ? 'true' : 'false');
    } else if (data[key] instanceof Object) {
      formData.append(key, JSON.stringify(data[key]));
    } else if (data[key] === '') {
      formData.append(key, data[key]);
    } else if (data[key]) {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

export const getGotoQueryParams = function (url: string) {
  const [url1, query] = url.split("?");
  if (query && query.toLowerCase().includes(LOGIN_GOTO_QUERY_PARAM.toLowerCase())) {
    url = url1;
  }
  const result = {};
  if (url !== '' && url !== '/' && url !== '/login') {
    result[LOGIN_GOTO_QUERY_PARAM] = url;
  }
  return result;
};

/** Remove legacy return URL written by older app versions (URL `?goto=` is authoritative now). */
export function clearLegacyGoto(): void {
  localStorage.removeItem('goto');
}

/** Post-login destination: only honor explicit `?goto=` on the login URL. */
export function resolvePostLoginUrl(gotoFromQuery: string | undefined | null): string {
  clearLegacyGoto();
  return getGotoQueryParams(gotoFromQuery || '/')[LOGIN_GOTO_QUERY_PARAM] || '/';
}

/** Build login queryParams with `goto` for redirect-after-login (URL only, not localStorage). */
export function persistGotoForLogin(url: string): Record<string, string> {
  const u = (url != null ? String(url) : '').trim() || '/';
  return getGotoQueryParams(u) as Record<string, string>;
}

/**
 * Scroll the routed page back to the top.
 *
 * The window itself never scrolls in this app — `#content-wrapper` (site-layout)
 * is the element with `overflow-y: auto`, which is also why the router's
 * `scrollPositionRestoration` has no visible effect. Scroll that element, and
 * fall back to the document scroller for any context where it is absent.
 */
export function scrollContentToTop(): void {
  const wrapper = typeof document !== 'undefined' ? document.getElementById('content-wrapper') : null;
  if (wrapper) {
    wrapper.scrollTop = 0;
    return;
  }
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
}

export const mapUploadProgress = map((event: HttpEvent<any>): HttpProgressEvent | HttpResponse<any> | undefined => {
  switch (event.type) {
    case HttpEventType.UploadProgress:
      return event;
    case HttpEventType.Response:
      return event;
  }
  return undefined;
});

export const getColumnIndexes = function (length: number, columns: number): number[] {
  const ret: number[] = [];
  const n = Math.floor(length / columns);
  for (let i = 0; i < columns; i++) {
    const prev = i === 0 ? 0 : ret[i - 1];
    if (i < length % columns) {
      ret.push(prev + n + 1);
    } else {
      ret.push(prev + n);
    }
  }
  return ret;
};

export const getPastMonthFirstDate = function () {
  return new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
};

export const getLastSixFirstDate = function () {
  return new Date(new Date().getFullYear(), new Date().getMonth() - 6, 1);
};

export const getLast12MonthFirstDate = function () {
  return new Date(new Date().getFullYear(), new Date().getMonth() - 12, 1);
};

export const getCurrentYearFirstDate = function () {
  return new Date(new Date().getFullYear(), 0, 1);
};

export const getCurrentYearTodaysDate = function () {
  return new Date();
};

export const getCurrentYearLastDay = function () {
  return new Date(new Date().getFullYear(), 12, 0);
};

export const getLastYearFirstDay = function () {
  return new Date(new Date().getFullYear() - 1, 0, 1);
};

export const getLastYearLastDay = function () {
  return new Date(new Date().getFullYear() - 1, 12, 0);
};

export const getDateFilterMenuItems = function () {
  return [
    { label: 'Past Month', start_date: getPastMonthFirstDate(), end_date: getCurrentYearTodaysDate() },
    { label: 'Past 6 Month', start_date: getLastSixFirstDate(), end_date: getCurrentYearTodaysDate() },
    { label: 'Past 12 Month', start_date: getLast12MonthFirstDate(), end_date: getCurrentYearTodaysDate() },
    { label: 'Current Year', start_date: getCurrentYearFirstDate(), end_date: getCurrentYearLastDay() },
    { label: 'Prior Year', start_date: getLastYearFirstDay(), end_date: getLastYearLastDay() },
  ];
};

export const getCurrentQuarter = function (startMonth) {
  const currentMonth = new Date().getMonth();
  if (currentMonth >= startMonth) {
    return Math.floor((currentMonth - startMonth) / 3) + 1;
  } else {
    return Math.floor((currentMonth - startMonth + 12) / 3) + 1;
  }
};

export const capitalizeFirstLetter = function (string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const isClosedOrReconciled = function (subStatusId?: string): boolean {
  return !!subStatusId && (subStatusId.endsWith('closed') || subStatusId.endsWith('reconciled'));
};

/** Staff dashboard task: company srg.checklist on row, else global catalog entry. */
export const deriveAssigneeStaffField = function (eventCardCategoryId: string): string {
  const category = String(eventCardCategoryId || '').toLowerCase();
  if (category === 'location') {
    return 'venue_staff';
  }
  if (category === 'speaker' || category === 'speaker-travel') {
    return 'faculty_staff';
  }
  return 'planner_staff';
};

const withAssigneeStaffField = function (checklist: any): any {
  if (!checklist) {
    return null;
  }
  if (checklist.assignee_staff_field) {
    return checklist;
  }
  const categoryId = checklist.event_card_category_id
    || checklist.event_card_category?.id
    || checklist.event_card_category?.label;
  if (!categoryId) {
    return checklist;
  }
  return {
    ...checklist,
    assignee_staff_field: deriveAssigneeStaffField(categoryId),
  };
};

export const resolveTaskChecklist = function (task: any, checklistsCatalog: any): any {
  if (!task) {
    return null;
  }
  const checklist = task.checklist_template || checklistsCatalog?.[task.checklist_id] || null;
  return withAssigneeStaffField(checklist);
};

export const getTaskAssigneeStaffId = function (task: any, checklistsCatalog: any): string | null {
  const checklist = resolveTaskChecklist(task, checklistsCatalog);
  if (!checklist?.assignee_staff_field || !task?.event) {
    return null;
  }
  return task.event[`${checklist.assignee_staff_field}_id`] ?? null;
};

export const isTaskAssignedToStaff = function (
  task: any,
  checklistsCatalog: any,
  staffId: string,
): boolean {
  if (!staffId) {
    return false;
  }
  const assigneeStaffId = getTaskAssigneeStaffId(task, checklistsCatalog);
  return assigneeStaffId != null && String(assigneeStaffId) === String(staffId);
};

export const getDashboardTaskKey = function (task: any): string {
  return `${task?.company_id}:${task?.event_id}:${task?.checklist_id}`;
};

export const dedupeDashboardTasks = function (tasks: any[]): any[] {
  const seen = new Set<string>();
  return (tasks || []).filter((task) => {
    const key = getDashboardTaskKey(task);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const filterDashboardTasksForStaff = function (
  tasks: any[],
  checklistsCatalog: any,
  staffId: string | null | undefined,
  limitToAssignee: boolean,
): any[] {
  if (!limitToAssignee) {
    return tasks;
  }
  if (!staffId) {
    return [];
  }
  return tasks.filter((task) => isTaskAssignedToStaff(task, checklistsCatalog, staffId));
};

/** Drop dashboard tasks on closed/reconciled events and stale pre-event tasks after start. */
export const shouldExcludeDashboardTask = function (task: any, now: Date = new Date()): boolean {
  const event = task?.event;
  if (!event) {
    return false;
  }

  if (isClosedOrReconciled(event.sub_status_id)) {
    return true;
  }

  if (event.event_date_tbd) {
    return false;
  }

  const startAt = event.common_event?.start_at;
  if (!startAt || !task.due_at) {
    return false;
  }

  const eventStart = new Date(startAt);
  if (now <= eventStart) {
    return false;
  }

  return new Date(task.due_at) < eventStart;
};

export const filterDashboardTasksByEventRules = function (
  tasks: any[],
  now: Date = new Date(),
): any[] {
  return (tasks || []).filter((task) => !shouldExcludeDashboardTask(task, now));
};

export const filterVisibleDashboardTasks = function (
  tasks: any[],
  checklistsCatalog: any,
  staffId: string | null | undefined,
  limitToAssignee: boolean,
  now: Date = new Date(),
): any[] {
  return filterDashboardTasksByEventRules(
    filterDashboardTasksForStaff(tasks, checklistsCatalog, staffId, limitToAssignee),
    now,
  );
};

export const getDashboardTaskEventStartAt = function (task: any): string | Date | null {
  if (!task || task.due_na || task.event?.event_date_tbd) {
    return null;
  }
  return task.event?.common_event?.start_at || task.event_date || null;
};

export const getDashboardTaskEventTimezone = function (task: any): string {
  return task?.event?.common_event?.timezone_id || 'America/New_York';
};

export const getTaskChecklistCategoryLabel = function (checklist: any): string {
  if (!checklist) {
    return '';
  }
  return checklist.event_card_category?.label || checklist.event_card_category_id || '';
};

/** First/last name are always required for contact profile create/update. */
export const hasRequiredContactNames = function (person: {
  first_name?: string | null;
  last_name?: string | null;
} | null | undefined): boolean {
  return !!(String(person?.first_name ?? '').trim() && String(person?.last_name ?? '').trim());
};
